import { FlaskConical, KeyRound, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'

import { ConfirmButton } from '@/components/ConfirmButton'
import { PageHeader, StatusBadge } from '@/components/ds'
import { EmptyState } from '@/components/EmptyState'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/copy-button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { StorefrontToken, StorefrontTokenCreated } from '@/interfaces/eshop'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'
import { formatDate } from '@/lib/formats/formatDate'
import { formatTime } from '@/lib/formats/formatTime'
import { cn } from '@/lib/utils'

export const StorefrontTokens = ({
  tokens,
  mutate,
}: {
  tokens: StorefrontToken[]
  mutate: () => void
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'eshop.tokens',
  })
  const { t: tg } = useTranslation(undefined, {
    keyPrefix: 'generic',
  })

  const [revokingToken, setRevokingToken] = useState<string | null>(null)
  const [revealOpen, setRevealOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [name, setName] = useState('')
  const [createdToken, setCreatedToken] =
    useState<StorefrontTokenCreated | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const cancelCreate = () => {
    setIsCreating(false)
    setName('')
  }

  const onCreate = async () => {
    const trimmedName = name.trim()

    if (!trimmedName) {
      toast.error(t('form.name.errors.required'))
      return
    }

    setIsSaving(true)

    return API.post<StorefrontTokenCreated>('/api/v2/storefront-tokens', {
      name: trimmedName,
    })
      .then((response) => {
        cancelCreate()
        setCreatedToken(response.data)
        setRevealOpen(true)
        toast.success(t('actions.create.success'))
      })
      .catch((error: unknown) => {
        toast.error(t('actions.create.error'), {
          description: extractErrorMessage(error),
        })
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  const onRevealClose = (open: boolean) => {
    setRevealOpen(open)

    if (!open) {
      setCreatedToken(null)
      mutate()
    }
  }

  const onNameKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      onCreate()
    }

  }

  const onRevoke = async (tokenId: string) => {
    return API.delete(`/api/v2/storefront-tokens/${tokenId}`)
      .then(() => {
        setRevokingToken(null)
        mutate()
        toast.success(t('actions.revoke.success'))
      })
      .catch((error: unknown) => {
        toast.error(t('actions.revoke.error'), {
          description: extractErrorMessage(error),
        })
      })
  }

  const activeCount = tokens.filter((token) => !token.revokedAt).length

  return (
    <div className="mx-auto w-full max-w-[1280px]">
      <PageHeader
        title={t('title')}
        description={
          <>
            Tokeny ověřují váš e-shop vůči storefront API. Posílejte je v hlavičce{' '}
            <code className="rounded-sm bg-muted px-1 py-0.5 font-mono text-xs text-foreground">X-Storefront-Token</code>.
          </>
        }
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/eshop/tester">
                <FlaskConical />
                {t('actions.tester.title')}
              </Link>
            </Button>
            <Button type="button" onClick={() => setIsCreating(true)}>
              <Plus />
              {t('actions.create.title')}
            </Button>
          </>
        }
      />

      <Dialog open={isCreating} onOpenChange={(open) => (open ? setIsCreating(true) : cancelCreate())}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('actions.create.title')}</DialogTitle>
            <DialogDescription>Pojmenujte token podle e-shopu, který ho bude používat.</DialogDescription>
          </DialogHeader>
          <label className="space-y-1.5">
            <span className="text-sm font-medium">{t('table.name')}</span>
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={onNameKeyDown}
              placeholder={t('form.name.placeholder')}
              autoComplete="off"
              autoFocus
            />
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={cancelCreate} disabled={isSaving}>
              {tg('button.cancel')}
            </Button>
            <Button onClick={onCreate} disabled={isSaving}>
              {t('actions.create.title')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={revealOpen} onOpenChange={onRevealClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('reveal.title')}</DialogTitle>
            <DialogDescription>{t('reveal.description')}</DialogDescription>
          </DialogHeader>
          {createdToken && (
            <div className="flex items-center gap-2 break-all rounded-md border bg-muted p-3 font-mono text-sm">
              <span className="min-w-0 flex-1">{createdToken.token}</span>
              <CopyButton content={createdToken.token} variant="outline" />
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => onRevealClose(false)}>
              {t('reveal.dismiss')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {tokens.length === 0 ? (
        <EmptyState icon={KeyRound}
          title="Zatím nemáte žádný token"
          description="Vytvořte token a vložte ho do konfigurace svého e-shopu."
          ctaButton={
            <Button onClick={() => setIsCreating(true)}>
              <Plus />
              {t('actions.create.title')}
            </Button>
          }
        />
      ) : (
        <>
          <p className="mb-3 text-sm text-muted-foreground tabular">
            {activeCount} aktivních z {tokens.length}
          </p>
          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t('table.name')}</TableHead>
                  <TableHead className="hidden w-48 md:table-cell">{t('table.createdAt')}</TableHead>
                  <TableHead className="md:w-48">{t('table.status')}</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {tokens.map((token) => (
                  <TableRow key={token.id} className="group">
                    <TableCell
                      className={cn(
                        'w-full max-w-0 md:w-auto md:max-w-none',
                        token.revokedAt ? 'text-muted-foreground' : 'font-medium',
                      )}
                    >
                      <span className="block truncate md:inline">{token.name}</span>
                      <p className="truncate text-xs font-normal text-muted-foreground tabular md:hidden">
                        {formatDate(token.createdAt)} {formatTime(token.createdAt)}
                      </p>
                    </TableCell>
                    <TableCell className="hidden tabular text-muted-foreground md:table-cell">
                      {formatDate(token.createdAt)} {formatTime(token.createdAt)}
                    </TableCell>
                    <TableCell>
                      {token.revokedAt ? (
                        <span className="inline-flex items-center gap-2">
                          <StatusBadge status="cancelled" label={t('status.revoked')} />
                          <span className="hidden text-xs text-muted-foreground tabular md:inline">{formatDate(token.revokedAt)}</span>
                        </span>
                      ) : (
                        <StatusBadge status="paid" label={t('status.active')} />
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {!token.revokedAt && (
                        <ConfirmButton
                          title={t('actions.revoke.title')}
                          description={t('actions.revoke.description')}
                          open={revokingToken === token.id}
                          onOpenChange={(open: boolean) => {
                            if (!open) {
                              setRevokingToken(null)
                            }
                          }}
                          onConfirm={() => onRevoke(token.id)}
                        >
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t('actions.revoke.title')}
                            className="transition-opacity hover:text-danger md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100"
                            onClick={() => setRevokingToken(token.id)}
                          >
                            <Trash2 />
                          </Button>
                        </ConfirmButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  )
}
